import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Power, Trash2 } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { ResourceActionsDropdown } from "./ResourceActionsDropdown";
import { ChangeStatusDialog } from "./ChangeStatusDialog";
import { ChangeVmIpDialog } from "./ChangeVmIpDialog";
import type { ChangeVmIpSubmit } from "./ChangeVmIpDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DeleteResourceDialog } from "./DeleteResourceDialog";
import { ResourceListStatusBadge } from "./ResourceStatusBadge";
import { isRestorableDeletion } from "@/lib/providerVm";
import { ReinstallResourceDialog } from "./ReinstallResourceDialog";
import { BulkStopResourcesDialog } from "./BulkStopResourcesDialog";
import { DeleteExpiredVmsDialog } from "@/pages/helper/expired-vms/DeleteExpiredVmsDialog";
import { RetryProvisioningDialog } from "./RetryProvisioningDialog";
import {
  BusyOverlay,
  PROVIDER_WAIT_HINT,
} from "@/components/common/BusyOverlay";
import {
  useBulkStopResources,
  useDeleteResource,
  useResourcePowerAction,
  useReinstallResource,
  useUpdateResourceStatus,
  useUpdateResourcePublicIp,
  useApplyVmIpAddress,
  useResourceStatusWatch,
  useProvisioningRetryCheck,
  getProvisioningRetryCheck,
  useRetryResourceProvisioning,
  LIST_STATUS_WATCH_MS,
} from "@/hooks/useResourceQueries";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PermissionGate } from "@/components/common/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import { formatCurrency } from "@/lib/currency";
import {
  RESOURCE_COLUMNS,
  useResourceColumnsStore,
  type ResourceColumnId,
} from "@/stores/resourceColumnsStore";
import type {
  BulkStopResourcesResult,
  DeleteResourcePayload,
  Resource,
  ResourceStatus,
} from "@/types/admin";

/**
 * "2 vCPU · 4 GB" — the machine's size in one line.
 *
 * Memory arrives in MB because that is the unit the specs are stored in, but nobody reads a VM as
 * "4096 MB": it is shown in GB, with one decimal only when the value is not a whole number (1.5 GB
 * is real; "4.0 GB" is noise). Either half can be missing on its own, so each is dashed
 * independently rather than dropping the whole line.
 */
function formatCpuAndMemory(
  vcpu: number | null,
  memoryMb: number | null,
): string {
  const cpu = vcpu !== null ? `${vcpu} vCPU` : "— vCPU";

  if (memoryMb === null) {
    return `${cpu} · — GB`;
  }

  const memoryGb = memoryMb / 1024;
  const formatted = Number.isInteger(memoryGb)
    ? memoryGb.toString()
    : memoryGb.toFixed(1);

  return `${cpu} · ${formatted} GB`;
}

/**
 * "cheap · Intel · ws-t4-i" — the product line and chip type, and the WiService pool they resolve
 * to.
 *
 * Rendered as nothing at all when neither is known: both are read out of the ordered
 * specifications, and roughly half the existing VMs were created before the wizard offered a chip
 * type, so a dash-filled line would be the most common thing in the column. Either half can be
 * missing on its own, and whichever survives is shown alone rather than dropping the pair.
 */
function ProvisioningPoolLine({ resource }: { resource: Resource }) {
  const pool = [resource.productLine, resource.chipType, resource.poolName]
    .filter((part): part is string => part != null && part.trim() !== "")
    .join(" · ");

  if (pool === "") {
    return null;
  }

  return (
    <div className="truncate text-[11px] text-muted-foreground">{pool}</div>
  );
}

/**
 * How wide each column is drawn.
 *
 * Kept here rather than in the store because a width is presentation, not preference — the store
 * holds only what gets written to localStorage. Typing it as a full `Record` over the id union is
 * the point: adding a column to `RESOURCE_COLUMNS` without giving it a width is a compile error,
 * not a column that silently collapses.
 *
 * Fixed px (via the spacing scale), not percentages: Name/Tenant/Order need room for full ids
 * without truncating, and the excess becomes a horizontal scrollbar instead of squeezing every
 * column into the viewport.
 */
const COLUMN_WIDTHS: Record<ResourceColumnId, string> = {
  select: "w-12",
  index: "w-14",
  name: "w-84",
  // Fits the longest caption, "Decommissioning" with its icon, without running into User.
  status: "w-38",
  user: "w-55",
  expiredAt: "w-40",
  specs: "w-36",
  network: "w-44",
  type: "w-36",
  order: "w-64",
  tenant: "w-72",
  createdAt: "w-40",
  actions: "w-20",
};

/**
 * Whether a row may be picked for the bulk stop.
 *
 * Far more permissive than the delete gate, and on purpose: the admin stop endpoint applies no
 * status gate at all — whether a machine can take a stop is the hypervisor's answer to give, and it
 * gives it per row. An operator reaches for it precisely when WiCloud's status and the real machine
 * disagree, so refusing here on that status would block the repair. Only the two kinds are ruled
 * out: a row that is not a virtual machine has no power state for the provider to act on, and one
 * already being deleted has nothing left to stop.
 */
/** Largest bulk delete one request takes: `AdminBulkDeleteResourceCommandValidator.MaximumResourceIds`. */
const BULK_DELETE_MAX = 10;

/**
 * A VM the bulk delete can act on: one still live, not mid-provisioning or already on its way out. A
 * Pending row is an unpaid order with no machine, which the backend refuses.
 */
function canSelectForDelete(resource: Resource): boolean {
  return (
    canSelectForStop(resource) &&
    resource.status !== "Provisioning" &&
    resource.status !== "Pending"
  );
}

function canSelectForStop(resource: Resource): boolean {
  return (
    !isDeleted(resource) &&
    resource.type === "VirtualMachine" &&
    resource.status !== "Terminating" &&
    resource.status !== "Terminated" &&
    resource.status !== "Decommissioning"
  );
}

/**
 * Whether the row is a deleted resource. The list shows them next to live ones (2026-10-14), so a
 * Decommissioning row is a deleted VM not finalized yet when this is true, and a reinstall or restore
 * in flight when it is not.
 */
function isDeleted(resource: Resource): boolean {
  return Boolean(resource.deletedAt);
}

/** Whether a row may be ticked at all. The bulk stop is the only bulk action. */
function canSelect(resource: Resource): boolean {
  return canSelectForStop(resource);
}

/**
 * The icon that tells apart the two meanings of Decommissioning, now that the caption no longer
 * does, with the explanation on hover. Every other status shows no icon.
 */
function statusIcon(resource: Resource): ReactNode {
  if (resource.status !== "Decommissioning") {
    return null;
  }

  const deleted = isDeleted(resource);
  const icon = deleted ? (
    <Trash2 className="h-3 w-3 shrink-0" aria-hidden="true" />
  ) : (
    <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
  );
  const explanation = deleted
    ? "Deleted: the machine is already gone at the provider. WiCloud keeps the record for 1 hour, while it can still be restored, then finalizes it to Terminated. Still here after that? Use “Finalize deletion now”."
    : "Reinstall or restore in progress: the machine is being rebuilt on the same UUID. It turns Running when the provider reports it running, or Error after 10 minutes without it.";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={explanation}
          className="inline-flex cursor-help"
        >
          {icon}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{explanation}</TooltipContent>
    </Tooltip>
  );
}

/** Says, in one phrase, why a row cannot be picked at all — shown as the checkbox's tooltip. */
function whyNotSelectable(resource: Resource): string | undefined {
  if (canSelect(resource)) {
    return undefined;
  }

  if (isDeleted(resource)) {
    return "Already deleted.";
  }

  if (resource.status === "Terminating" || resource.status === "Terminated") {
    return "Already being deleted.";
  }

  if (resource.status === "Decommissioning") {
    return "Being reinstalled.";
  }

  return "Only virtual machines can be stopped.";
}

interface ResourceMonitoringTableProps {
  resources: Resource[];
  /** Row number of the first resource in `resources`, e.g. (page - 1) * pageSize. */
  startIndex?: number;
  /**
   * Bump this to drop the current selection.
   *
   * A counter rather than a callback because the selection lives here: the page owns the reset
   * action, not the state. Needed because the effect below only prunes rows that disappeared, and
   * a refresh brings the same rows back - so a selection made before it survived into a list the
   * operator had just asked to start over.
   */
  selectionResetKey?: number;

  /**
   * What to show in place of the table when the list came back empty. The page owns the wording —
   * only it knows whether a filter emptied the list — but it must not own the BRANCH: rendering
   * this instead of the table would unmount the dialogs and the busy overlay below, mid-action.
   */
  emptyState?: ReactNode;
}

export function ResourceMonitoringTable({
  resources,
  startIndex = 0,
  selectionResetKey = 0,
  emptyState,
}: ResourceMonitoringTableProps) {
  const navigate = useNavigate();
  const [resourceToDelete, setResourceToDelete] = useState<Resource | null>(
    null,
  );
  const [resourceToReaddress, setResourceToReaddress] =
    useState<Resource | null>(null);
  const [resourceToRestatus, setResourceToRestatus] = useState<Resource | null>(
    null,
  );
  // The live VM the in-place reinstall dialog is open for (design doc §5a).
  const [reinstallTarget, setReinstallTarget] = useState<Resource | null>(null);
  const [resourceToRetryProvisioning, setResourceToRetryProvisioning] =
    useState<Resource | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkStopDialogOpen, setBulkStopDialogOpen] = useState(false);
  // The VMs the bulk delete dialog was opened on; null while it is closed.
  const [bulkDeleteVms, setBulkDeleteVms] = useState<Resource[] | null>(null);
  const [bulkStopResult, setBulkStopResult] =
    useState<BulkStopResourcesResult | null>(null);
  // Which columns are on screen. Subscribed here, set from the toolbar's Columns menu - both talk
  // to the same store rather than threading props through the page.
  //
  // Subscribing to `hidden` and deriving the predicate locally, NOT subscribing to the store's own
  // `isVisible`: that one is a stable function reading the latest state, so a component that
  // subscribes to it re-renders for nothing that changed and never re-renders when a column is
  // toggled. `hidden` is the value that actually moves.
  const hiddenColumns = useResourceColumnsStore((state) => state.hidden);
  const isVisible = (id: ResourceColumnId) => !hiddenColumns.includes(id);

  const deleteResource = useDeleteResource();
  // The delete dialog's own power steps (shut down / resume). SuperAdmin only, like every power
  // action; for anyone else the dialog shows the steps without a button.
  const deleteDialogPowerAction = useResourcePowerAction();
  const { isSuperAdmin } = usePermissions();
  const reinstallResource = useReinstallResource();
  const bulkStop = useBulkStopResources();
  const updateStatus = useUpdateResourceStatus();
  const updatePublicIp = useUpdateResourcePublicIp();
  const applyVmIp = useApplyVmIpAddress();
  const retryProvisioning = useRetryResourceProvisioning();
  const busyLabel = deleteResource.isPending
    ? "Deleting resource…"
    : reinstallResource.isPending
      ? "Starting reinstall…"
      : bulkStop.isPending
        ? "Stopping the selected resources…"
        : updateStatus.isPending
          ? "Updating status…"
          : applyVmIp.isPending
            ? "Applying the IP address to the machine…"
            : updatePublicIp.isPending
              ? "Updating IP address…"
              : retryProvisioning.isPending
                ? "Retrying provisioning…"
                : null;

  // A Terminating row waits on n8n in the background. Only its status is re-read; the list is
  // refetched once, when one of them is deleted or put back.
  const terminatingIds = useMemo(
    () =>
      resources
        .filter((resource) => resource.status === "Terminating")
        .map((resource) => resource.id),
    [resources],
  );
  // A Decommissioning row that is not deleted is a reinstall in flight (§3 of the design doc); a
  // deleted one waits for its finalize, which no watch here follows.
  const reinstallingIds = useMemo(
    () =>
      resources
        .filter(
          (resource) =>
            resource.status === "Decommissioning" && !isDeleted(resource),
        )
        .map((resource) => resource.id),
    [resources],
  );
  useResourceStatusWatch(
    [...terminatingIds, ...reinstallingIds],
    LIST_STATUS_WATCH_MS,
    undefined,
    reinstallingIds,
  );

  // One batch check for every Error VM on this page — open to every admin role — rather than one
  // call per row. Most pages have none, so the query stays disabled.
  const errorVmIds = useMemo(
    () =>
      resources
        .filter(
          (resource) =>
            resource.status === "Error" && resource.type === "VirtualMachine",
        )
        .map((resource) => resource.id),
    [resources],
  );
  const { data: retryChecks } = useProvisioningRetryCheck(errorVmIds);

  const selectableIds = useMemo(
    () => resources.filter(canSelect).map((resource) => resource.id),
    [resources],
  );

  // Selection is per page view. Changing the page or a filter replaces the rows under it, and a
  // selection that outlived them would delete machines the operator can no longer see — the exact
  // mistake the dialog's row-by-row list exists to prevent.
  useEffect(() => {
    setSelectedIds((previous) => {
      const stillVisible = previous.filter((id) => selectableIds.includes(id));

      // Same array unless something actually dropped out: returning a new one on every render
      // would loop, since this effect depends on the ids it sets.
      return stillVisible.length === previous.length ? previous : stillVisible;
    });
  }, [selectableIds]);

  // Drops the selection when the page asks for a reset. Separate from the pruning effect above:
  // that one answers "these rows are gone", this one answers "the operator started over".
  useEffect(() => {
    setSelectedIds([]);
  }, [selectionResetKey]);

  const selectedResources = useMemo(
    () => resources.filter((resource) => selectedIds.includes(resource.id)),
    [resources, selectedIds],
  );

  const stoppableSelected = useMemo(
    () => selectedResources.filter(canSelectForStop),
    [selectedResources],
  );

  const deletableSelected = useMemo(
    () => selectedResources.filter(canSelectForDelete),
    [selectedResources],
  );
  const bulkDeleteTooMany = deletableSelected.length > BULK_DELETE_MAX;

  const allSelectableSelected =
    selectableIds.length > 0 && selectedIds.length === selectableIds.length;

  const toggleRow = (resourceId: string, checked: boolean) => {
    setSelectedIds((previous) =>
      checked
        ? [...previous, resourceId]
        : previous.filter((id) => id !== resourceId),
    );
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? selectableIds : []);
  };

  const handleConfirmBulkStop = (reason: string) => {
    bulkStop.mutate(
      {
        resourceIds: stoppableSelected.map((resource) => resource.id),
        reason: reason === "" ? undefined : reason,
      },
      {
        onSuccess: (result) => {
          // Only the machines that actually stopped leave the selection. What is left is exactly
          // what the hypervisor refused, so the operator can read why and act on those rows
          // without re-picking them out of the table.
          const stopped = new Set(
            result.results
              .filter((row) => row.success)
              .map((row) => row.resourceId),
          );
          setSelectedIds((previous) =>
            previous.filter((id) => !stopped.has(id)),
          );

          if (result.failureCount === 0) {
            setBulkStopResult(null);
            setBulkStopDialogOpen(false);
            return;
          }

          // Kept open on a partial batch: which rows were refused, and the provider's sentence for
          // each, is more than a toast can hold and is the only thing that says what to do next.
          setBulkStopResult(result);
        },
      },
    );
  };

  const handleConfirmDelete = (
    payload: DeleteResourcePayload,
    recordOnly: boolean,
  ) => {
    if (!resourceToDelete) return;

    // The dialog stays open while the request runs so the spinner covers it; a failure leaves it
    // up so the operator can read the toast and retry with what they typed still in the fields.
    // A restorable VM stays in this list as Terminating — the hook words the toast for that.
    const restorable = isRestorableDeletion(
      resourceToDelete.type,
      resourceToDelete.providerResourceId,
      !recordOnly,
    );
    deleteResource.mutate(
      { id: resourceToDelete.id, payload, recordOnly, restorable },
      { onSuccess: () => setResourceToDelete(null) },
    );
  };

  const handleSubmitStatus = (status: ResourceStatus) => {
    if (!resourceToRestatus) return;

    updateStatus.mutate(
      { id: resourceToRestatus.id, status },
      { onSuccess: () => setResourceToRestatus(null) },
    );
  };

  const handleConfirmReinstall = (payload: {
    templateId?: string;
    confirmationName: string;
    reason: string;
  }) => {
    if (!reinstallTarget) return;

    reinstallResource.mutate(
      { id: reinstallTarget.id, payload },
      { onSuccess: () => setReinstallTarget(null) },
    );
  };

  // One handler for both layers: the dialog's payload says which endpoint it wants, and the two
  // mutations differ in what happens afterwards. The record path is done when it returns, so the
  // dialog closes; the machine path switches the dialog to its result view, which is the only place
  // a generated password is ever readable — so it is deliberately left open.
  const handleSubmitIp = (payload: ChangeVmIpSubmit) => {
    if (!resourceToReaddress) return;

    if (payload.target === "record") {
      updatePublicIp.mutate(
        {
          id: resourceToReaddress.id,
          ipAddress: payload.ipAddress,
          reason: payload.reason,
        },
        { onSuccess: () => setResourceToReaddress(null) },
      );
      return;
    }

    applyVmIp.mutate({
      id: resourceToReaddress.id,
      payload: {
        ipAddress: payload.ipAddress,
        gateway: payload.gateway,
        resetPassword: payload.resetPassword,
        reason: payload.reason,
      },
    });
  };

  const getTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      VirtualMachine: "bg-info-soft text-info-ink",
      Storage: "bg-ok-soft text-ok-ink",
      ObjectStorage: "bg-ok-soft text-ok-ink",
      Network: "bg-brand-soft text-brand-ink",
      LoadBalancer: "bg-brand-soft text-brand-ink",
      Database: "bg-warn-soft text-warn-ink",
    };
    return colors[type] || "bg-secondary text-muted-foreground";
  };

  // Kept, commented out, because the Order column now shows the order TYPE instead of its status.
  // `resource.orderStatus` still arrives on the wire, so restoring the status badge is this block
  // plus the badge in the Order cell - nothing else. Mirrors AdminOrderDetailPage's palette so the
  // same status reads the same color whether an admin is looking at the order or at a resource
  // created from it.
  //
  // const getOrderStatusColor = (status: string): string => {
  //   const colors: Record<string, string> = {
  //     Draft: "bg-secondary text-muted-foreground",
  //     Pending: "bg-warn-soft text-warn-ink",
  //     Paid: "bg-info-soft text-info-ink",
  //     Processing: "bg-brand-soft text-brand-ink",
  //     Complete: "bg-ok-soft text-ok-ink",
  //     Failed: "bg-destructive-soft text-destructive-ink",
  //     Cancelled: "bg-secondary text-muted-foreground",
  //     Refunded: "bg-secondary text-muted-foreground",
  //   };
  //   return colors[status] || "bg-secondary text-muted-foreground";
  // };

  // Held in a variable so BOTH branches below render it. These dialogs and this overlay own the
  // state of an action in flight, and the page used to drop this whole component the moment the
  // list came back empty — which is exactly what a successful action on the last remaining row
  // produces (a restorable VM leaving the "Restorable" view, the last row of a page being
  // deleted). The operator's confirmation dialog and the "please wait" overlay vanished mid-write,
  // and the callbacks passed to `mutate` never ran, because React Query drops them when the
  // observer unmounts — taking the bulk-stop result summary with them.
  // The row as the list has it now, not as it was when Delete was clicked: a shutdown from the
  // dialog refetches the list, and the dialog must see the VM reach Stopped to move on.
  const liveResourceToDelete =
    resourceToDelete === null
      ? null
      : (resources.find((row) => row.id === resourceToDelete.id) ??
        resourceToDelete);
  const runDeleteDialogPowerAction =
    liveResourceToDelete !== null && isSuperAdmin()
      ? (action: "shutdown") => () =>
          deleteDialogPowerAction.mutateAsync({
            id: liveResourceToDelete.id,
            action,
          })
      : null;

  const dialogsAndOverlay = (
    <>
      <DeleteResourceDialog
        open={resourceToDelete !== null}
        onOpenChange={(open) => !open && setResourceToDelete(null)}
        resourceName={resourceToDelete?.name ?? ""}
        resourceType={resourceToDelete?.type ?? ""}
        resourceStatus={liveResourceToDelete?.status ?? ""}
        providerResourceId={resourceToDelete?.providerResourceId}
        expiresAt={resourceToDelete?.expiresAt}
        tenantName={resourceToDelete?.tenantName}
        isPending={deleteResource.isPending}
        onConfirm={handleConfirmDelete}
        error={deleteResource.error}
        onShutDown={runDeleteDialogPowerAction?.("shutdown")}
        isPowerActionPending={deleteDialogPowerAction.isPending}
      />

      <DeleteExpiredVmsDialog
        vms={bulkDeleteVms}
        recordOnly={false}
        defaultReason="Clean up VM"
        onClose={(deletedIds) => {
          setBulkDeleteVms(null);
          // The deleted rows leave the selection; the refused ones stay picked to act on.
          setSelectedIds((previous) =>
            previous.filter((id) => !deletedIds.includes(id)),
          );
        }}
      />

      <BulkStopResourcesDialog
        open={bulkStopDialogOpen}
        onOpenChange={(open) => {
          setBulkStopDialogOpen(open);

          if (!open) {
            setBulkStopResult(null);
          }
        }}
        resources={stoppableSelected}
        isPending={bulkStop.isPending}
        result={bulkStopResult}
        onConfirm={handleConfirmBulkStop}
      />

      <ChangeStatusDialog
        resource={resourceToRestatus}
        onClose={() => setResourceToRestatus(null)}
        onSubmit={handleSubmitStatus}
        isSubmitting={updateStatus.isPending}
      />

      <ChangeVmIpDialog
        resource={resourceToReaddress}
        onClose={() => {
          setResourceToReaddress(null);

          // Clears the result view so reopening the dialog on any row starts on the form again,
          // rather than on the previous row's answer.
          applyVmIp.reset();
        }}
        onSubmit={handleSubmitIp}
        isSubmitting={updatePublicIp.isPending || applyVmIp.isPending}
        result={applyVmIp.data}
      />

      {/* The row writes started from a dialog cover the table's Card (the page's `relative` Card is
          the nearest positioned ancestor) while they run: the table behind the dialog — another
          row's menu, the bulk bar — must not take a second write before the re-read shows what this
          one did, while the rest of the console stays usable. Power actions and the reminder are
          not here: they spin the row's own menu trigger; recovery runs on its own page. */}
      <BusyOverlay
        contained
        open={busyLabel !== null}
        label={busyLabel ?? ""}
        hint={
          applyVmIp.isPending || bulkStop.isPending
            ? PROVIDER_WAIT_HINT
            : undefined
        }
      />

      {/* Mounted only once a row is picked. */}
      {resourceToRetryProvisioning && (
        <RetryProvisioningDialog
          open
          onOpenChange={(open) => !open && setResourceToRetryProvisioning(null)}
          onUpdateByUuidInstead={() =>
            navigate(
              `/admin/resources/${resourceToRetryProvisioning.id}/update-by-uuid`,
            )
          }
          resourceName={resourceToRetryProvisioning.name}
          status={resourceToRetryProvisioning.status}
          tenantName={resourceToRetryProvisioning.tenantName}
          createdAt={resourceToRetryProvisioning.createdAt}
          mayAlreadyExist={
            getProvisioningRetryCheck(
              retryChecks,
              resourceToRetryProvisioning.id,
            )?.machineMayExist ?? false
          }
          recordedVmUuid={resourceToRetryProvisioning.providerResourceId}
          isPending={retryProvisioning.isPending}
          onConfirm={({ recreateMissingVm }) => {
            retryProvisioning.mutate(
              { id: resourceToRetryProvisioning.id, recreateMissingVm },
              { onSettled: () => setResourceToRetryProvisioning(null) },
            );
          }}
        />
      )}

      {/* Mounted only once a row is picked, so opening it is what fetches the OS image catalog.
          Read the row fresh from `resources` by id on every render rather than trusting the
          click-time snapshot in `reinstallTarget`: the status watch above invalidates the list
          while this dialog is open (Stopped -> Decommissioning once the reinstall starts, or a
          status changed from elsewhere), and a stale snapshot would keep showing a gate that no
          longer matches the live row — `ResourceDetailPage` avoids this by reading `resource.status`
          straight off its own query. Falls back to the snapshot for the brief window after the row
          leaves the current page (e.g. a filter change), where its id is no longer in `resources`. */}
      {reinstallTarget &&
        (() => {
          const row =
            resources.find((r) => r.id === reinstallTarget.id) ??
            reinstallTarget;
          return (
            <ReinstallResourceDialog
              open
              onOpenChange={(open) => !open && setReinstallTarget(null)}
              resourceName={row.name}
              currentOperatingSystem={row.operatingSystem}
              vmCores={row.vcpu}
              vmMemoryMb={row.memoryMb}
              vmDiskGb={row.diskSizeGb}
              resourceStatus={row.status}
              providerResourceId={row.providerResourceId}
              expiresAt={row.expiresAt}
              isPending={reinstallResource.isPending}
              error={reinstallResource.error}
              onConfirm={handleConfirmReinstall}
            />
          );
        })()}
    </>
  );

  if (resources.length === 0) {
    return (
      <>
        {emptyState ?? (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">No resources found</p>
          </div>
        )}
        {dialogsAndOverlay}
      </>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {/* Only once something is picked: an empty bar above every table would take a row of height
          from a screen whose whole job is fitting as many machines on it as possible. */}
      {selectedIds.length > 0 && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2">
          {/* One text node, not a bolded count next to a string: a screen reader and a test both
              read this as one sentence, and splitting it makes "2 resources selected" unfindable. */}
          <p className="text-sm font-medium">
            {`${selectedIds.length} resource${selectedIds.length === 1 ? "" : "s"} selected`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds([])}
              disabled={bulkStop.isPending}
            >
              Clear
            </Button>
            {/* Same gate as the single delete and the single power actions in the row menu: both
                endpoints are SuperAdmin-only in their handlers, so showing the buttons to anyone
                else would only produce a 403. */}
            <PermissionGate roles="SuperAdmin">
              {/* Counted, like the delete beside it: the selection can hold rows this cannot act on
                  (a disk has no power state), and the number is what says how many will be sent. */}
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={bulkStop.isPending || stoppableSelected.length === 0}
                onClick={() => {
                  setBulkStopResult(null);
                  setBulkStopDialogOpen(true);
                }}
              >
                <Power className="h-4 w-4" />
                {`Stop ${stoppableSelected.length} VM${stoppableSelected.length === 1 ? "" : "s"}`}
              </Button>
              {/* The same bulk delete as the Expired VMs page, with no lapse filter: each VM goes
                  through the ordinary admin delete and the report lists every refusal. */}
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-destructive-ink hover:text-destructive-ink"
                disabled={
                  bulkStop.isPending ||
                  deletableSelected.length === 0 ||
                  bulkDeleteTooMany
                }
                title={
                  bulkDeleteTooMany
                    ? `At most ${BULK_DELETE_MAX} VMs can be deleted at once`
                    : undefined
                }
                onClick={() => setBulkDeleteVms(deletableSelected)}
              >
                <Trash2 className="h-4 w-4" />
                {`Delete ${deletableSelected.length} VM${deletableSelected.length === 1 ? "" : "s"}`}
              </Button>
            </PermissionGate>
          </div>
        </div>
      )}

      {/*
        `[&>div]:overflow-visible` targets the wrapper `components/ui/table.tsx` puts around every
        table (`<div class="relative w-full overflow-auto">`), and it is what makes the horizontal
        scrollbar reachable. That wrapper is as tall as the whole table (measured: 1339px against a
        418px visible box), so while IT owned the horizontal overflow its scrollbar rendered below
        the last row - a full screen under the viewport, where nobody could reach it. Switching it
        off hands both axes to this div, the one with a bounded height, so the bar sits at the
        bottom of the visible table. It also repairs the sticky header below: `position: sticky`
        resolves against the nearest scrollport, and while that was the inner wrapper - a div that
        never scrolls vertically - the header simply scrolled away.
      */}
      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
        {/* One provider for every status icon's tooltip on the page. */}
        <TooltipProvider delayDuration={150}>
          <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
            {/*
          Fixed px widths (not %) on purpose: Name/Tenant/Order need room to show full
          IDs/names without truncating, and the wrapper's overflow-auto turns the excess
          into a horizontal scrollbar instead of squeezing every column to fit the viewport.
          A fixed-layout table is widened to the sum of these widths (2178px), so no `min-w` is
          needed to make it overflow - only a scrollport that puts the bar somewhere visible.
        */}
            {/* Generated from RESOURCE_COLUMNS rather than written out, so a hidden column cannot
              leave its <col> behind - which would shift every width after it by one and is the
              one drift the browser gives no error for. */}
            <colgroup>
              {RESOURCE_COLUMNS.filter((column) => isVisible(column.id)).map(
                (column) => (
                  <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
                ),
              )}
            </colgroup>
            <TableHeader className="sticky top-0 z-30 bg-card">
              <TableRow>
                {isVisible("select") && (
                  <TableHead className="text-center">
                    {/* Selects every row on THIS page a bulk action can act on, not every row: a
                      header box that ticks rows nothing can be done to turns "select all" into a
                      list of failures. */}
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={allSelectableSelected}
                        disabled={selectableIds.length === 0}
                        onCheckedChange={(checked) =>
                          toggleAll(checked === true)
                        }
                        aria-label="Select all actionable resources on this page"
                        className="cursor-pointer"
                      />
                    </div>
                  </TableHead>
                )}
                {isVisible("index") && (
                  <TableHead className="text-center">STT</TableHead>
                )}
                {isVisible("name") && <TableHead>Resource Name</TableHead>}
                {isVisible("status") && <TableHead>Status</TableHead>}
                {isVisible("user") && <TableHead>User</TableHead>}
                {isVisible("expiredAt") && <TableHead>ExpiredAt</TableHead>}
                {isVisible("specs") && <TableHead>Specs</TableHead>}
                {isVisible("network") && <TableHead>IP / OS / CPU</TableHead>}
                {isVisible("type") && <TableHead>Type</TableHead>}
                {isVisible("order") && <TableHead>Order</TableHead>}
                {isVisible("tenant") && <TableHead>Tenant</TableHead>}
                {isVisible("createdAt") && <TableHead>Created At</TableHead>}
                {isVisible("actions") && (
                  <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                    Actions
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {resources.map((resource, index) => (
                <TableRow
                  key={resource.id}
                  data-state={
                    selectedIds.includes(resource.id) ? "selected" : undefined
                  }
                >
                  {isVisible("select") && (
                    <TableCell className="text-center">
                      <div
                        className="flex items-center justify-center"
                        title={whyNotSelectable(resource)}
                      >
                        <Checkbox
                          checked={selectedIds.includes(resource.id)}
                          disabled={!canSelect(resource)}
                          onCheckedChange={(checked) =>
                            toggleRow(resource.id, checked === true)
                          }
                          aria-label={`Select ${resource.name}`}
                          className="cursor-pointer"
                        />
                      </div>
                    </TableCell>
                  )}
                  {isVisible("index") && (
                    <TableCell className="text-center text-muted-foreground">
                      {startIndex + index + 1}
                    </TableCell>
                  )}
                  {isVisible("name") && (
                    <TableCell>
                      <div className="space-y-1">
                        {/* A deleted row has no detail page: it is hidden from the lookup behind it. */}
                        {isDeleted(resource) ? (
                          <p
                            title={resource.name}
                            className="truncate font-medium"
                          >
                            {resource.name}
                          </p>
                        ) : (
                          <button
                            onClick={() =>
                              navigate(`/admin/resources/${resource.id}`)
                            }
                            title={resource.name}
                            className="block w-fit max-w-full cursor-pointer truncate text-left font-medium text-primary hover:underline"
                          >
                            {resource.name}
                          </button>
                        )}
                        {resource.providerResourceId && (
                          <p className="truncate text-xs text-muted-foreground">
                            UUID: {resource.providerResourceId}
                          </p>
                        )}
                      </div>
                    </TableCell>
                  )}
                  {isVisible("status") && (
                    <TableCell>
                      {/* The stored status as is (2026-10-14), the word the database and logs carry. */}
                      <ResourceListStatusBadge
                        status={resource.status}
                        icon={statusIcon(resource)}
                      />
                    </TableCell>
                  )}
                  {isVisible("user") && (
                    <TableCell>
                      {resource.userFullName && (
                        <div className="truncate font-medium">
                          {resource.userFullName}
                        </div>
                      )}
                      <div className="truncate">{resource.userEmail}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {resource.userPhoneNumber ?? "—"}
                      </div>
                    </TableCell>
                  )}
                  {isVisible("expiredAt") && (
                    <TableCell>
                      {resource.expiresAt && (
                        <div className="truncate text-xs">
                          {formatDateTime(resource.expiresAt)}
                        </div>
                      )}
                      {resource.monthlyPrice > 0 && (
                        <div className="truncate text-xs text-muted-foreground">
                          {formatCurrency(
                            resource.monthlyPrice,
                            resource.currency,
                          )}
                          /mo
                        </div>
                      )}
                    </TableCell>
                  )}
                  {isVisible("specs") && (
                    <TableCell>
                      {/*
                  CPU and RAM on one line, disk under it: the pair says how big the machine is,
                  the disk says how much it stores, and an operator scanning the column reads the
                  size first. A dash rather than a zero where a value is missing — a non-VM row has
                  no CPU at all, and a VM mid-provisioning has no disk size yet.
                */}
                      {resource.vcpu !== null || resource.memoryMb !== null ? (
                        <>
                          <div className="truncate text-xs">
                            {formatCpuAndMemory(
                              resource.vcpu,
                              resource.memoryMb,
                            )}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {resource.diskSizeGb !== null
                              ? `${resource.diskSizeGb} GB disk`
                              : "— disk"}
                          </div>
                        </>
                      ) : resource.attachmentSummary ? (
                        /*
                        A Disk or Network row has no CPU, but it is not featureless: this is its
                        size and tier, or its VLAN. Every column here used to read "—" on those
                        rows while the resource's own JSON held all of it, so an operator scanning
                        a stack could see that a disk existed and nothing about it.
                      */
                        <div className="truncate text-xs">
                          {resource.attachmentSummary}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                  {isVisible("network") && (
                    <TableCell>
                      {/*
                  Public IP is what an operator looks for, but a VM on an internal-only network
                  never gets one - fall back to the private address and say so, rather than
                  leaving the row blank when an address does exist. Non-VM resources have neither.

                  Under it: what the machine runs, and what it runs on. Region used to sit here and
                  said "vn-south-1" on every single row - one value across the whole fleet answers
                  no question anyone asks of this screen, while the OS and the pool the VM landed
                  in (product line + chip) are what an operator is actually scanning for. Region is
                  still on the resource detail page.
                */}
                      <div className="space-y-1">
                        {(resource.publicIpAddress ??
                        resource.privateIpAddress) ? (
                          <div className="flex items-center gap-1">
                            <code className="truncate text-xs">
                              {resource.publicIpAddress ??
                                resource.privateIpAddress}
                            </code>
                            {!resource.publicIpAddress && (
                              <span className="text-[11px] text-muted-foreground">
                                private
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        )}
                        <div className="truncate text-xs">
                          {resource.operatingSystem ?? "—"}
                        </div>
                      </div>
                    </TableCell>
                  )}
                  {isVisible("type") && (
                    <TableCell>
                      {/* The pool line sits here rather than under the IP: "cheap · Intel" is what the
                      machine is, which is the question this column already answers. */}
                      <div className="space-y-1">
                        <Badge
                          variant="outline"
                          className={`px-1.5 py-0 text-[11px] ${getTypeColor(resource.type)}`}
                        >
                          {resource.type}
                        </Badge>
                        <ProvisioningPoolLine resource={resource} />
                      </div>
                    </TableCell>
                  )}
                  {isVisible("order") && (
                    <TableCell>
                      {/* Order type, not order status: the status repeats what the resource's own
                      Status column already implies, while "NewService" vs "Renewal" says why this
                      row exists at all - and it is the only place on the screen that does. */}
                      {resource.orderNumber ? (
                        <>
                          <div className="truncate">{resource.orderNumber}</div>
                          {resource.orderType && (
                            <Badge
                              variant="outline"
                              className="mt-1 px-1.5 py-0 text-[11px] text-muted-foreground"
                            >
                              {resource.orderType}
                            </Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                  {isVisible("tenant") && (
                    <TableCell>
                      <div className="truncate">{resource.tenantName}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {resource.tenantId}
                      </div>
                    </TableCell>
                  )}
                  {isVisible("createdAt") && (
                    <TableCell className="text-muted-foreground">
                      {/* Who created it, under when. Usually the owner in the User column, and worth
                      the line for the rows where it is not - a VM an operator provisioned on a
                      customer's behalf looks identical otherwise. */}
                      <div>{formatDateTime(resource.createdAt)}</div>
                      <div className="truncate text-xs">
                        {resource.createdByEmail ?? "—"}
                      </div>
                    </TableCell>
                  )}
                  {isVisible("actions") && (
                    <TableCell className="sticky right-0 z-10 bg-card shadow-[inset_1px_0_0_0_var(--border)]">
                      <div className="flex items-center justify-center">
                        <ResourceActionsDropdown
                          resource={resource}
                          onUpdateByUuid={() =>
                            navigate(
                              `/admin/resources/${resource.id}/update-by-uuid`,
                            )
                          }
                          onChangeExpiry={() =>
                            navigate(`/admin/resources/${resource.id}/expiry`)
                          }
                          onChangeStatus={() => setResourceToRestatus(resource)}
                          onChangeIp={() => setResourceToReaddress(resource)}
                          onDelete={() => setResourceToDelete(resource)}
                          isDeleting={
                            deleteResource.isPending &&
                            deleteResource.variables?.id === resource.id
                          }
                          onReinstallLive={() => setReinstallTarget(resource)}
                          isDeleted={isDeleted(resource)}
                          retryCheck={getProvisioningRetryCheck(
                            retryChecks,
                            resource.id,
                          )}
                          onRetryProvisioning={() =>
                            setResourceToRetryProvisioning(resource)
                          }
                        />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TooltipProvider>
      </div>

      {dialogsAndOverlay}
    </div>
  );
}
